/**
 * ChequeoDetail - Vista detallada de una evaluación completada
 * Features:
 * - Información de la evaluación
 * - Respuestas por ítem
 * - Comentarios
 * - Fecha y evaluador
 */
import { useState, useEffect } from 'react';
import {
  Calendar,
  User,
  FileText,
  CheckCircle,
  XCircle,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import { chequeoAPI } from '../../api';
import { Loading, Alert, Button } from '../common';

const SECCIONES = {
  I: { nombre: 'Privacidad, Datos Personales y Transparencia', color: 'blue' },
  II: { nombre: 'Algoritmos, Sesgo y Equidad', color: 'purple' },
  III: { nombre: 'Impacto Social y Comunitario', color: 'green' },
  IV: { nombre: 'Gobernanza y Cultura de Compliance', color: 'orange' },
};

const ChequeoDetail = ({ evaluacion: evaluacionProp }) => {
  const [evaluacion, setEvaluacion] = useState(evaluacionProp);
  const [plantilla, setPlantilla] = useState([]);
  const [loading, setLoading] = useState(!evaluacionProp);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!evaluacionProp) {
      loadEvaluacion();
    } else {
      loadPlantilla();
    }
  }, [evaluacionProp]);

  const loadEvaluacion = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await chequeoAPI.getEvaluacion(evaluacionProp.id_evaluacion);
      setEvaluacion(response.data);
      await loadPlantilla();
    } catch (err) {
      console.error('Error al cargar evaluación:', err);
      setError(err.message || 'Error al cargar evaluación');
    } finally {
      setLoading(false);
    }
  };

  const loadPlantilla = async () => {
    try {
      const response = await chequeoAPI.getPlantilla();
      setPlantilla(response.data);
    } catch (err) {
      console.error('Error al cargar plantilla:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando evaluación..." />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" title="Error" message={error} />;
  }

  if (!evaluacion) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No se encontró la evaluación</p>
      </div>
    );
  }

  // Agrupar respuestas por ítem
  const respuestasPorItem = {};
  if (evaluacion.respuestas) {
    evaluacion.respuestas.forEach((r) => {
      respuestasPorItem[r.id_item] = r;
    });
  }

  // Agrupar ítems por sección
  const itemsPorSeccion = plantilla.reduce((acc, item) => {
    const seccion = item.seccion;
    if (!acc[seccion]) {
      acc[seccion] = [];
    }
    acc[seccion].push(item);
    return acc;
  }, {});

  // Calcular estadísticas
  const totalRespuestas = Object.keys(respuestasPorItem).length;
  const respuestasSi = Object.values(respuestasPorItem).filter(
    (r) => r.respuesta === true
  ).length;
  const respuestasNo = Object.values(respuestasPorItem).filter(
    (r) => r.respuesta === false
  ).length;

  return (
    <div className="space-y-6">
      {/* Header con información general */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              Evaluación de Lista de Chequeo
            </h3>
            <p className="text-gray-600 mt-1">ID: {evaluacion.id_evaluacion}</p>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              evaluacion.completada
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {evaluacion.completada ? '✓ Completada' : '⏳ En Progreso'}
          </span>
        </div>

        {/* Información de evaluación */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg">
              <User className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Evaluador</p>
              <p className="font-medium text-gray-900">
                {evaluacion.usuario_evaluador?.nombre_completo || 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg">
              <Calendar className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha de Evaluación</p>
              <p className="font-medium text-gray-900">
                {new Date(evaluacion.fecha_evaluacion).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas de respuestas */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-5 w-5 text-gray-600" />
            <span className="text-sm text-gray-600">Total Respuestas</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalRespuestas}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Respuestas Sí</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{respuestasSi}</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-red-700">Respuestas No</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{respuestasNo}</p>
        </div>
      </div>

      {/* Respuestas por sección */}
      <div className="space-y-6">
        {Object.keys(SECCIONES).map((seccionKey) => {
          const seccion = SECCIONES[seccionKey];
          const items = itemsPorSeccion[seccionKey] || [];

          if (items.length === 0) return null;

          return (
            <div key={seccionKey} className="space-y-4">
              {/* Header de sección */}
              <div
                className={`bg-${seccion.color}-50 border-l-4 border-${seccion.color}-500 p-4 rounded-r-lg`}
              >
                <h4 className={`text-lg font-semibold text-${seccion.color}-900`}>
                  Sección {seccionKey}: {seccion.nombre}
                </h4>
              </div>

              {/* Items */}
              <div className="space-y-3">
                {items.map((item, index) => {
                  const respuesta = respuestasPorItem[item.id_item];

                  return (
                    <div
                      key={item.id_item}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      {/* Pregunta */}
                      <div className="flex items-start justify-between mb-3">
                        <p className="font-medium text-gray-900 flex-1">
                          {index + 1}. {item.descripcion}
                        </p>
                        {respuesta && (
                          <span
                            className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ml-4 ${
                              respuesta.respuesta
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {respuesta.respuesta ? (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                Sí
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4" />
                                No
                              </>
                            )}
                          </span>
                        )}
                      </div>

                      {/* Comentario si existe */}
                      {respuesta?.comentario && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-700">{respuesta.comentario}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Notas generales */}
      {evaluacion.notas_generales && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-gray-600" />
            <h4 className="text-lg font-semibold text-gray-900">Notas Generales</h4>
          </div>
          <p className="text-gray-700 whitespace-pre-wrap">
            {evaluacion.notas_generales}
          </p>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => window.print()}
        >
          Imprimir
        </Button>
      </div>
    </div>
  );
};

export default ChequeoDetail;
